import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import type {
  GetMerchantStatusQueryParams,
  GetMerchantStatusResponse,
  PostCheckoutCreateRequest,
  PostCheckoutCreateResponse,
  PostCreateCheckoutIntentRequest,
  PostCreateCheckoutIntentResponse,
} from '@appTypes/payment';
import { IS_LOCAL_ENV, getPaymentsUrl } from '@config/config';
import { tagTypes } from '@modules/rtk/constants';
import tagFactory from '@modules/rtk/utils/tagFactory';

export const paymentApi = createApi({
  reducerPath: 'paymentApi',
  tagTypes: Object.keys(tagTypes),
  baseQuery: fetchBaseQuery({
    // Uncomment in local dev
    credentials: IS_LOCAL_ENV ? 'omit' : 'include',
    baseUrl: getPaymentsUrl(),
    prepareHeaders: (headers, api) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const jwt = (api.getState() as any).user?.loginData?.token;
      if (jwt) {
        headers.set('Authorization', `Bearer ${jwt}`);
      }
      return headers;
    },
  }),
  endpoints: builder => ({
    getMerchantStatus: builder.query<
      GetMerchantStatusResponse,
      {
        params: GetMerchantStatusQueryParams;
      }
    >({
      query: arg => ({
        url: '/api/stripe/merchantStatus',
        method: 'GET',
        params: arg.params,
      }),
      providesTags: (result, error, arg) => {
        if (!result || error) return [];

        const tags = [
          tagFactory.MerchantStatuses.organizationStatus(
            `${arg.params.merchant_num}`, // Organization GUID
          ),
        ];

        return tags;
      },
    }),

    createCheckoutIntent: builder.mutation<
      PostCreateCheckoutIntentResponse,
      {
        payload: PostCreateCheckoutIntentRequest;
      }
    >({
      query: arg => ({
        url: '/api/stripe/createCheckoutIntent',
        method: 'POST',
        body: arg.payload,
      }),
    }),

    checkoutCreate: builder.mutation<
      PostCheckoutCreateResponse,
      {
        payload: PostCheckoutCreateRequest;
      }
    >({
      query: arg => ({
        url: '/api/stripe/checkoutCreate',
        method: 'POST',
        body: arg.payload,
        validateStatus: (response, body) => {
          const httpStatusOK = response.status >= 200 && response.status <= 299;
          const apiResponseCode = body.status;
          return httpStatusOK && apiResponseCode;
        },
      }),
    }),
  }),
});

export const {
  useGetMerchantStatusQuery,
  useCreateCheckoutIntentMutation,
  useCheckoutCreateMutation,
} = paymentApi;
